import React, {useContext, useState} from 'react';
import {Box, Card, CardMedia, Grid, useMediaQuery} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useSelector} from "react-redux";
import {selectSessionUser} from "../../../../../../store/authentication/selectors";
import constants from "../../../../../../config/constants";
import {GameStateContext} from "../GeneratedHome";

// Regular Card Components
const CardContainer = styled(Grid)(({theme}) => ({
    display: 'flex',
    justifyContent: 'center',
}));

// Updated StyledCard to accept isSelectable and isSelected props for styling
const StyledCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'isSelectable' && prop !== 'isSelected'
})(({theme, isSelectable, isSelected}) => ({
    width: '100%',
    height: '100%',
    position: 'relative',
    transition: 'transform 0.3s ease',
    backgroundColor: isSelected ? theme.palette.info.dark : isSelectable ? theme.palette.info.main : 'inherit',
    border: isSelected ? `3px solid ${theme.palette.info.dark}` : 'none',
    cursor: isSelectable ? 'pointer' : 'default',
    '&:hover': {
        transform: isSelectable ? 'scale(1.05)' : 'none',
        zIndex: isSelectable ? 10 : 'auto',
    },
}));

// Card image that maintains aspect ratio
const CardImage = styled(CardMedia)(({theme, isVertical}) => ({
    paddingTop: isVertical ? '120%' : '140%', // Different aspect ratio based on orientation
    backgroundSize: 'contain',
}));

// Column-based layout for overlapping cards
const CardColumn = styled(Box)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
}));

// Updated OverlappingCard to accept isSelectable and isSelected props
const OverlappingCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'isSelectable' && prop !== 'isSelected' && prop !== 'index'
})(({theme, index, isSelectable, isSelected}) => ({
    width: '100%',
    marginTop: index === 0 ? 0 : `-${70}%`, // Overlap cards by 70%
    transition: 'transform 0.2s ease-in-out',
    position: 'relative',
    zIndex: index,
    boxShadow: theme.shadows[3],
    backgroundColor: isSelected ? theme.palette.info.dark : isSelectable ? theme.palette.info.main : '#10346f',
    border: isSelected ? `3px solid ${theme.palette.primary.main}` : 'none',
    cursor: isSelectable ? 'pointer' : 'default',
    '&:hover': {
        transform: isSelectable ? 'translateY(-10px) scale(1.05)' : 'none',
        zIndex: isSelectable ? 100 : index,
        boxShadow: isSelectable ? theme.shadows[10] : theme.shadows[3],
    },
}));

/**
 * ConsultCardScreen Component
 *
 * A responsive component for displaying a grid of cards
 * @param {Object} props
 * @param {Object} props.screen - Object containing cards data
 * @param {Array} props.screen.cards - Array of card objects with cardId and imageName
 * @param {String} props.gameId
 */
export const ConsultCardScreen = ({screen, gameId}) => {
    // Get current user
    const user = useSelector(selectSessionUser);

    // Use GameStateContext to access the cardSelectionActions and setState
    const {state, setState} = useContext(GameStateContext);
    const cardSelectionActions = state?.cardSelectionActions || [];

    // State for tracking selected cards
    const [selectedCardIds, setSelectedCardIds] = useState([]);

    // Check if we're on a mobile device and screen orientation
    const isMobile = useMediaQuery('(max-width:600px)');
    const isSmallScreen = useMediaQuery('(max-width:900px)');
    const isVertical = useMediaQuery('(orientation: portrait)');
    const isHighCardCount = screen?.cards?.length > 12;

    // Base URL for the card images
    const baseImageUrl = constants.api.basePath + "/generated/" + gameId + "/image";

    // Handle the case when no screen data or no cards are provided
    const cards = screen?.cards ? [...screen.cards].sort((a, b) => a.imageName.localeCompare(b.imageName)) : [];

    if (cards.length === 0) return null;

    // Find actions that allow selecting exactly one card
    const getSingleCardSelectionAction = (cardId) => {
        if (cardSelectionActions.length === 0) return null;

        for (const action of cardSelectionActions) {
            if (action.class === "model.action.special.ActionSelectCards" &&
                action.possibleCardIds &&
                action.possibleCardIds.includes(cardId) &&
                action.nbMinCardsToSelect === 1 &&
                action.nbMaxCardsToSelect === 1) {
                return action;
            }
        }
        return null;
    };

    // Find actions that allow selecting multiple cards
    const getMultiCardSelectionAction = (cardId) => {
        if (cardSelectionActions.length === 0) return null;

        for (const action of cardSelectionActions) {
            if (action.class === "model.action.special.ActionSelectCards" &&
                action.possibleCardIds &&
                action.possibleCardIds.includes(cardId) &&
                (action.nbMaxCardsToSelect > 1 || action.nbMinCardsToSelect > 1)) {
                return action;
            }
        }
        return null;
    };

    // Check if a card is selectable based on the cardSelectionActions
    const isCardSelectable = (cardId) => {
        return getSingleCardSelectionAction(cardId) !== null || getMultiCardSelectionAction(cardId) !== null;
    };

    // Check if a card is currently selected
    const isCardSelected = (cardId) => {
        return selectedCardIds.includes(cardId);
    };

    // Handle card click - handles both single and multi selection
    const handleCardClick = (card) => {
        // Check for single card selection action
        const singleAction = getSingleCardSelectionAction(card.cardId);
        if (singleAction) {
            // Create a copy of the action with the selected card ID
            const actionToExecute = {...singleAction};
            actionToExecute.cards = [card];
            actionToExecute.name = `Jouer la carte ${card.imageName}`;

            // Use the parent's modal mechanism
            if (setState) {
                setState(currentState => ({...currentState, action: actionToExecute, show: true}));
            }
            return;
        }

        // Check for multi-card selection action
        const multiAction = getMultiCardSelectionAction(card.cardId);
        if (multiAction) {
            // Toggle the selection of this card
            let newSelectedCardIds = [...selectedCardIds];

            if (isCardSelected(card.cardId)) {
                // Remove from selection
                newSelectedCardIds = newSelectedCardIds.filter(id => id !== card.cardId);
            } else {
                // Add to selection
                newSelectedCardIds.push(card.cardId);
            }

            setSelectedCardIds(newSelectedCardIds);

            // Check if we have the right number of cards selected
            if (newSelectedCardIds.length >= multiAction.nbMinCardsToSelect &&
                newSelectedCardIds.length <= multiAction.nbMaxCardsToSelect) {
                // We have the right number of cards, create an action
                const actionToExecute = {...multiAction};

                // Create a name that shows the number of selected cards
                const selectedCards = cards.filter(c => newSelectedCardIds.includes(c.cardId));
                actionToExecute.cards = selectedCards;
                const cardNames = selectedCards.map(c => c.imageName).join(", ");
                actionToExecute.name = `Jouer ${newSelectedCardIds.length} carte${newSelectedCardIds.length > 1 ? 's' : ''}: ${cardNames}`;

                // Use the parent's modal mechanism
                if (setState) {
                    setState(currentState => ({...currentState, action: actionToExecute, show: true}));

                    // Reset selection after showing the modal
                    setSelectedCardIds([]);
                }
            }
        }
    };

    // Determine grid sizing based on number of cards and screen size
    const getGridSize = () => {
        if (cards.length <= 4) return {xs: 4, sm: 4, md: 4, lg: 3}; // Always at least 3 columns (12/4=3)
        if (cards.length <= 8) return {xs: 4, sm: 4, md: 3, lg: 3};
        if (cards.length <= 15) return {xs: 4, sm: 4, md: 3, lg: 2};
        return {xs: 4, sm: 3, md: 2, lg: 2}; // For 16+ cards
    };

    // Create a responsive layout with overlapping cards in columns
    const renderOverlappingCardGrid = () => {
        // Always use at least 3 columns, or more for higher card counts
        const numColumns = isHighCardCount ? 4 : 3;

        // Create an array of columns
        const columns = Array(numColumns).fill().map(() => []);

        // Distribute cards among columns in a balanced way
        cards.forEach((card, index) => {
            const columnIndex = index % numColumns;
            columns[columnIndex].push(card);
        });

        return (
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
                gap: 1,
                width: '100%',
                padding: 1,
                boxSizing: 'border-box', // Ensure padding is included in width calculation
                maxWidth: '100%', // Prevent overflow
                overflowX: 'hidden', // Hide horizontal overflow
            }}>
                {columns.map((column, colIndex) => (
                    <CardColumn key={`col-${colIndex}`}>
                        {column.map((card, cardIndex) => (
                            <OverlappingCard
                                key={card.cardId}
                                index={cardIndex}
                                isSelectable={isCardSelectable(card.cardId)}
                                isSelected={isCardSelected(card.cardId)} // Pass isSelected prop
                                onClick={() => handleCardClick(card)} // Add click handler for overlapping cards
                            >
                                <CardImage
                                    image={`${baseImageUrl}?imagePath=cards/${encodeURIComponent(card.imageName)}.png`}
                                    title={`Card ${card.cardId}`}
                                    alt={`Card ${card.cardId}`}
                                    isVertical={true}
                                    sx={{
                                        paddingTop: '140%',
                                    }}
                                />
                            </OverlappingCard>
                        ))}
                    </CardColumn>
                ))}
            </Box>
        );
    };

    // Use the overlapping card layout for both desktop and mobile when we have many cards
    if (cards.length > 4) {
        return (
            <Box className="consult-card-screen-overlapping" sx={{
                width: '100%',
                padding: 1,
                boxSizing: 'border-box',
                maxWidth: '100%',
                overflowX: 'hidden',
            }}>
                {renderOverlappingCardGrid()}
            </Box>
        );
    }

    // Standard grid layout for few cards
    const gridSize = getGridSize();

    return (
        <Box className="consult-card-screen" sx={{
            width: '100%',
            padding: 1,
            boxSizing: 'border-box',
            maxWidth: '100%',
            overflowX: 'hidden',
        }}>
            <Grid container spacing={1} sx={{alignItems: 'flex-start'}}>
                {cards.map((card) => (
                    <CardContainer
                        item
                        xs={gridSize.xs}
                        sm={gridSize.sm}
                        md={gridSize.md}
                        lg={gridSize.lg}
                        key={card.cardId}
                        sx={{mb: 1}}
                    >
                        <StyledCard
                            isSelectable={isCardSelectable(card.cardId)}
                            isSelected={isCardSelected(card.cardId)} // Pass isSelected prop
                            onClick={() => handleCardClick(card)} // Handle card click
                        >
                            <CardImage
                                image={`${baseImageUrl}?imagePath=cards/${encodeURIComponent(card.imageName)}.png`}
                                title={`Card ${card.cardId}`}
                                alt={`Card ${card.cardId}`}
                                isVertical={isVertical}
                            />
                        </StyledCard>
                    </CardContainer>
                ))}
            </Grid>
        </Box>
    );
};

export default ConsultCardScreen;
